/**
 * Script autonome pour ajouter automatiquement le paramètre magasin 
 * à tous les liens de collection
 * 
 * Fonctionnalités :
 * - Détecte tous les liens contenant "collection"
 * - Ajoute automatiquement filter.p.tag=STOKOMANI+[magasin] depuis localStorage
 * - Met à jour quand le header change
 * - Gère les liens ajoutés dynamiquement
 */

(function() {
  'use strict';
  
  // Configuration
  const CONFIG = {
    debug: false, // Mettre à true pour voir les logs
    storageKey: 'selectedShop',
    headerSelector: '#store-finder-button-text',
    paramName: 'filter.p.tag',
    urlPattern: /\/collections\/|collection/i,
    updateInterval: 1000, // Vérification périodique en ms
    debounceDelay: 300 // Délai pour éviter les mises à jour trop fréquentes
  };
  
  // Variables globales
  let lastKnownStore = null;
  let processedLinks = new WeakSet();
  let isProcessing = false;
  let updateTimeout;
  
  // Fonction de logging
  function log(...args) {
    if (CONFIG.debug) {
      console.log('[AUTO-STORE-LINKS]', ...args);
    }
  }
  
  // Fonction pour extraire le nom du magasin depuis localStorage
  function getStoreNameFromStorage() {
    try {
      const selectedShopJson = localStorage.getItem(CONFIG.storageKey);
      if (selectedShopJson) {
        const selectedShop = JSON.parse(selectedShopJson);
        if (selectedShop && selectedShop.name && selectedShop.name.trim() !== '') {
          return selectedShop.name.trim();
        }
      }
    } catch (e) {
      log('Erreur lecture localStorage:', e);
    }
    return null;
  }
  
  // Fonction pour extraire le nom du magasin depuis le header
  function getStoreNameFromHeader() {
    const headerElement = document.querySelector(CONFIG.headerSelector);
    if (headerElement && headerElement.textContent.trim()) {
      const headerText = headerElement.textContent.trim();
      // Ne pas prendre "Trouver mon magasin" ou "Modifier"
      if (headerText !== 'Trouver mon magasin' && headerText !== 'Modifier' && !headerText.toLowerCase().includes('magasin')) {
        return headerText;
      }
    }
    return null;
  }
  
  // Fonction pour obtenir le nom du magasin actuel (prioriser localStorage)
  function getCurrentStoreName() {
    return getStoreNameFromStorage() || getStoreNameFromHeader();
  }
  
  // Fonction pour vérifier si un lien doit être traité
  function shouldProcessLink(link) {
    if (!link || !link.href) return false;
    
    // Vérifier si c'est un lien de collection
    if (!CONFIG.urlPattern.test(link.href)) return false;
    
    // Éviter les liens externes
    if (link.hostname && link.hostname !== window.location.hostname) return false;
    
    // Éviter certains liens spéciaux
    if (link.href.includes('#') || link.href.includes('javascript:')) return false;
    
    return true;
  }
  
  // Fonction pour ajouter/mettre à jour le paramètre magasin dans une URL
  function updateLinkWithStore(link, storeName) {
    if (!shouldProcessLink(link)) return false;
    
    try {
      const url = new URL(link.href);
      
      if (storeName) {
        // ✅ CORRECTION : Vérifier si le nom contient déjà "STOKOMANI"
        let storeValue;
        if (storeName.toUpperCase().includes('STOKOMANI')) {
          // Si le nom contient déjà STOKOMANI, l'utiliser tel quel
          storeValue = storeName;
        } else {
          // Sinon, ajouter STOKOMANI devant
          storeValue = `STOKOMANI ${storeName}`;
        }
        
        url.searchParams.set(CONFIG.paramName, storeValue);
        log(`Mise à jour lien: ${link.href} → ${url.toString()}`);
      } else {
        // Supprimer le paramètre si pas de magasin
        if (url.searchParams.has(CONFIG.paramName)) {
          url.searchParams.delete(CONFIG.paramName);
          log(`Suppression paramètre: ${link.href} → ${url.toString()}`);
        } else {
          return false; // Pas de changement nécessaire
        }
      }
      
      link.href = url.toString();
      processedLinks.add(link);
      return true;
    } catch (e) {
      log('Erreur mise à jour lien:', e, link.href);
      return false;
    }
  }
  
  // Fonction pour traiter tous les liens de la page
  function updateAllCollectionLinks() {
    if (isProcessing) return;
    isProcessing = true;
    
    const storeName = getCurrentStoreName();
    
    // Si le magasin n'a pas changé, ne pas traiter
    if (storeName === lastKnownStore) {
      isProcessing = false;
      return;
    }
    
    log('Mise à jour des liens avec magasin:', storeName);
    
    // Trouver tous les liens
    const links = document.querySelectorAll('a[href]');
    let updatedCount = 0;
    
    links.forEach(link => {
      if (updateLinkWithStore(link, storeName)) {
        updatedCount++;
      }
    });
    
    lastKnownStore = storeName;
    log(`${updatedCount} liens mis à jour`);
    
    isProcessing = false;
  }
  
  // Fonction debounced pour éviter les mises à jour trop fréquentes
  function debouncedUpdate() {
    clearTimeout(updateTimeout);
    updateTimeout = setTimeout(updateAllCollectionLinks, CONFIG.debounceDelay);
  }
  
  // Observer pour détecter les nouveaux liens ajoutés dynamiquement
  function setupDynamicLinksObserver() {
    const observer = new MutationObserver(function(mutations) {
      let hasNewLinks = false;
      
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Vérifier si le noeud ajouté est un lien ou contient des liens
              if (node.tagName === 'A' || node.querySelectorAll('a[href]').length > 0) {
                hasNewLinks = true;
              }
            }
          });
        }
      });
      
      if (hasNewLinks) {
        log('Nouveaux liens détectés, mise à jour...');
        debouncedUpdate();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    log('Observer pour nouveaux liens configuré');
  }
  
  // Observer pour détecter les changements du header
  function setupHeaderObserver() {
    const headerElement = document.querySelector(CONFIG.headerSelector);
    if (!headerElement) {
      log('Header element non trouvé, réessai dans 2s...');
      setTimeout(setupHeaderObserver, 2000);
      return;
    }
    
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          log('Changement détecté dans le header');
          debouncedUpdate();
        }
      });
    });
    
    observer.observe(headerElement, {
      childList: true,
      subtree: true,
      characterData: true
    });
    
    log('Observer header configuré');
  }
  
  // Observer pour détecter les changements localStorage
  function setupStorageObserver() {
    // Écouter les changements localStorage (depuis d'autres onglets)
    window.addEventListener('storage', function(e) {
      if (e.key === CONFIG.storageKey) {
        log('Changement localStorage détecté');
        debouncedUpdate();
      }
    });
    
    // Hack pour détecter les changements localStorage locaux
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      const oldValue = localStorage.getItem(key);
      originalSetItem.call(this, key, value);
      
      if (key === CONFIG.storageKey && value !== oldValue) {
        log('Changement localStorage local détecté');
        debouncedUpdate();
      }
    };
    
    log('Observer localStorage configuré');
  }
  
  // Fonction d'initialisation
  function initialize() {
    log('Initialisation du système de liens automatiques...');
    
    // Mise à jour initiale
    updateAllCollectionLinks();
    
    // Configuration des observers
    setupHeaderObserver();
    setupDynamicLinksObserver();
    setupStorageObserver();
    
    // Mise à jour périodique (backup)
    setInterval(function() {
      if (!isProcessing) {
        const currentStore = getCurrentStoreName();
        if (currentStore !== lastKnownStore) {
          log('Vérification périodique: magasin changé');
          updateAllCollectionLinks();
        }
      }
    }, CONFIG.updateInterval);
    
    log('✅ Système de liens automatiques initialisé');
    
    // Exposer des fonctions pour debug
    if (CONFIG.debug) {
      window.autoStoreLinks = {
        update: updateAllCollectionLinks,
        getCurrentStore: getCurrentStoreName,
        getProcessedCount: () => processedLinks.size || 0,
        debug: () => ({
          lastKnownStore,
          currentStore: getCurrentStoreName(),
          isProcessing,
          processedLinksCount: processedLinks.size || 0
        })
      };
    }
  }
  
  // Démarrage quand le DOM est prêt
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    // Petit délai pour s'assurer que les autres scripts sont chargés
    setTimeout(initialize, 100);
  }
  
  log('Script auto-store-links chargé');
  
})(); 